import React, { useContext, useState } from 'react';
import { userPrivileges } from '@apollo-internal/cache';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Button } from '@mui/material';
import ApolloError from '@shared/components/ApolloError';
import Asterisk from '@shared/components/Asterisk';
import CheckboxField from '@shared/components/CheckboxField';
import CustomDatePicker from '@shared/components/CustomDatePicker';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import StickyHeader from '@shared/components/StickyHeader';
import { getToday } from '@shared/services/dateService';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { PAGE_VIEW_MODES } from '../../../../constants/page-view-modes';
import PrivilegeNames from '../../../../constants/privilegeNames';
import { newLine } from '../../../../constants/textConstants';
import ErrorMessage from '@shared/components/ErrorMessage';
import LinkButton from '@shared/components/LinkButton';
import PageInstructions from '@shared/components/PageInstructions';
import { checkPointData } from '../../checkPointsCache';
import {
  MDDAP_CHECK_POINTDOCUMENT_TYPES,
  checkPointDocumentsMap
} from '../../constants/mddap-checkpoint-document-types';
import { CHECKPOINT_DOCUMENT_TYPES_PRIVILEGED_ONLY } from '../../constants/checkpoint-document-types-privileged-only';
import GET_CHECK_POINT_CONSTRAINTS from '../../queries/getCheckpointConstraints';
import SUBMIT_CHECK_POINT_DATA from '../../queries/submitCheckPointDataMutation';
import CheckPointMapper from '../mappers/checkPointMapper';
import * as CheckPointDataValidator from '../validators/CheckPointDataValidator';
import CheckpointDocumentsValidator from '../validators/CheckPointDocumentValidator';
import CheckpointFilesTable from './CheckpointFilesTable';
import PerformanceMetricsTable from './PerformanceMetricsTable';
import GET_CHECK_POINTS from '../../queries/getCheckPoints';
import SuccessMessage from '@shared/components/SuccessMessage';
import { useCheckPointFile } from '@shared/hooks/useCheckPointFile';
import { PageLoadingContext } from '../../../loader/contexts/PageLoadingContext';
import { FILE_SIZE } from '@shared/constants/fileSize';

/**
 * @typedef {object} CheckpointValidationErrors
 * @property {boolean} [hasDateError]
 * @property {boolean} [hasMetricsError]
 */

/**
 * @typedef {object} CheckpointFormProps
 * @property {string} previousLink
 * @property {string} description
 * @property {string} title
 * @property {string} [successfullyMessage]
 * @property {number} [orgUnitId]
 * @property {number} [appraisalId]
 * @property {string} viewMode
 * @property {boolean} loading
 * @property {number} checkPointId
 * @property {(event?: React.MouseEvent) => void} [handleFormSave]
 * @property {boolean} [autoSaveInProgress]
 * @property {() => void} [resetInterval]
 */

/**
 * @param {CheckpointFormProps} props
 */
const CheckpointForm = props => {
  const {
    previousLink,
    description,
    title,
    orgUnitId,
    appraisalId,
    viewMode,
    loading,
    checkPointId,
    handleFormSave,
    resetInterval,
    successfullyMessage = '',
    autoSaveInProgress = false
  } = props;

  const location = useLocation();

  const checkPointCache = useReactiveVar(checkPointData);
  const privileges = useReactiveVar(userPrivileges);
  const canUploadRequiredFiles = privileges.includes(
    PrivilegeNames.CheckPoints_UploadRequiredFiles
  );
  const canReviewReappraisalRationale = privileges.includes(
    PrivilegeNames.CheckPoints_ReappraisalRationaleReview
  );
  const navigate = useNavigate();

  const [invalidMetrics, setInvalidMetrics] = useState([]);
  const [validationErrors, setValidationErrors] = useState(
    /** @type {CheckpointValidationErrors} */ ({})
  );
  const [documentValidationErrors, setDocumentValidationErrors] = useState([]);
  const [fileErrorMessage, setFileErrorMessage] = useState(null);

  const { executeWithLoadingIndicator } = useContext(PageLoadingContext);

  const documentRequirements = [
    {
      documentType: MDDAP_CHECK_POINTDOCUMENT_TYPES.CheckPointReport,
      isRequired: dataCollectedDuringAppraisal =>
        !dataCollectedDuringAppraisal &&
        !checkPointCache.checkPointDocuments.some(
          x => x.documentType === MDDAP_CHECK_POINTDOCUMENT_TYPES.ReappraisalRationale
        ) &&
        canUploadRequiredFiles &&
        !(checkPointCache.hasReappraisalRationaleBeforeEditing && canReviewReappraisalRationale),
      validTypes: [
        MDDAP_CHECK_POINTDOCUMENT_TYPES.CheckPointReport,
        MDDAP_CHECK_POINTDOCUMENT_TYPES.ReappraisalRationale
      ]
    },
    {
      documentType: MDDAP_CHECK_POINTDOCUMENT_TYPES.ReappraisalRationale,
      isRequired: _ =>
        canUploadRequiredFiles &&
        canReviewReappraisalRationale &&
        checkPointCache.hasReappraisalRationaleBeforeEditing,
      validTypes: [MDDAP_CHECK_POINTDOCUMENT_TYPES.ReappraisalRationale]
    }
  ];

  const requiredDocuments = documentRequirements
    ?.filter(x => x.isRequired(checkPointCache.dataCollectedDuringAppraisal))
    .map(x => x.documentType);

  const { downloadFile, uploadFile } = useCheckPointFile();

  const {
    data: checkpointConstraintsData,
    error: checkpointAddingDataError,
    loading: addingCheckpointDataLoading
  } = useQuery(GET_CHECK_POINT_CONSTRAINTS, {
    variables: { orgUnitId: orgUnitId, appraisalId: appraisalId }
  });

  const minDate =
    checkpointConstraintsData && checkpointConstraintsData.checkpointConstraints
      ? new Date(checkpointConstraintsData.checkpointConstraints.MinDate)
      : null;
  const maxDate =
    checkpointConstraintsData && checkpointConstraintsData.checkpointConstraints
      ? getMaxDate(checkpointConstraintsData.checkpointConstraints)
      : null;

  const [submitCheckPoint, { error: submitCheckPointError }] = useMutation(
    SUBMIT_CHECK_POINT_DATA,
    {
      update: cache => cache.evict({ query: GET_CHECK_POINTS })
    }
  );

  const handleFormSubmit = async () => {
    if (viewMode === PAGE_VIEW_MODES.ADD) {
      resetInterval();
    }

    if (!validateForm(checkPointCache)) {
      return;
    }

    const checkPointDataForSubmit = canUploadRequiredFiles
      ? checkPointCache
      : {
          ...checkPointCache,
          checkPointDocuments: checkPointCache.checkPointDocuments.filter(
            doc => !CHECKPOINT_DOCUMENT_TYPES_PRIVILEGED_ONLY.includes(doc.documentType)
          )
        };

    const payload = CheckPointMapper.checkPointToApolloInput(checkPointDataForSubmit, checkPointId);

    await submitCheckPoint({ variables: { data: payload } });

    navigate(previousLink, {
      state: {
        checkPointModifiedSuccessfullyMessage: successfullyMessage
      }
    });
  };

  const validateForm = formData => {
    const checkPointValidations = getCheckPointValidations(formData);

    setValidationErrors({
      hasDateError: !checkPointValidations.isDateValid,
      hasMetricsError: !checkPointValidations.isMetricsValid
    });

    setInvalidMetrics(
      checkPointValidations.isMetricsValid ? [] : checkPointValidations.invalidMetrics
    );

    const isCheckpointDocumentsValid = validateCheckpointDocuments(
      formData.checkPointDocuments,
      formData.dataCollectedDuringAppraisal
    );

    return checkPointValidations.isValid && isCheckpointDocumentsValid;
  };

  const validateCheckpointDocuments = (
    documents,
    dataCollectedDuringAppraisal,
    updateErrorsWhenValid = false
  ) => {
    const validations = CheckpointDocumentsValidator.validateCheckpointDocuments(
      documents,
      dataCollectedDuringAppraisal,
      documentRequirements
    );

    const isValid = validations.length === 0;

    if (!updateErrorsWhenValid) {
      setDocumentValidationErrors(validations);
      return isValid;
    }

    if (isValid) {
      setDocumentValidationErrors(validations);
    }

    return isValid;
  };

  const getCheckPointValidations = checkPointData => {
    const metricsValidations = CheckPointDataValidator.isMetricsValid(
      checkPointData.performanceMetrics
    );

    const checkPointValidations = {
      isDateValid: CheckPointDataValidator.isDateValid(
        checkPointData.dataCollectionDate,
        minDate,
        maxDate
      ),
      isMetricsValid: metricsValidations.isMetricsValid,
      invalidMetrics: metricsValidations.invalidMetrics
    };

    checkPointValidations.isValid =
      checkPointValidations.isDateValid && checkPointValidations.isMetricsValid;

    return checkPointValidations;
  };

  const handleChangeMetricValues = updatedMetric => {
    const updatedMetricData = updateMetricCachedData(updatedMetric);
    const updatedCheckPointData = {
      ...checkPointCache,
      performanceMetrics: updatedMetricData
    };
    updateMetricErrorMessages(updatedCheckPointData);
    checkPointData(updatedCheckPointData);
  };

  const updateMetricCachedData = updatedMetric => {
    const data = [...checkPointCache.performanceMetrics];
    const index = data.findIndex(pm => pm.tempId === updatedMetric.tempId);
    data[index] = updatedMetric;
    return data;
  };

  const updateMetricErrorMessages = data => {
    if (!validationErrors.hasMetricsError) {
      return;
    }
    validateForm(data);
  };

  const handleFileInputClicked = (e, record) => {
    const file = e.target.files[0];
    if (!validateFile(file, record)) {
      return;
    }

    handleFileUpload(e, file, record);
  };

  const getAllowedFileUploadSize = () => {
    if (!checkpointConstraintsData) {
      return 0;
    }

    return checkpointConstraintsData.checkpointConstraints.AllowedFileUploadSize;
  };

  const validateFile = (file, record) => {
    const fileType = file.type;
    const fileSize = file.size;
    const extension = file.name.split('.').pop();
    const allowedFileUploadSize = getAllowedFileUploadSize();

    let errorMessage = '';

    const documentInfo = checkPointDocumentsMap.get(record.documentType);

    if (checkPointCache.checkPointDocuments.find(x => x.fileName === file.name)) {
      errorMessage += 'Cannot upload two files with same name.' + newLine;
    }

    if (fileSize > allowedFileUploadSize * FILE_SIZE.MB) {
      errorMessage +=
        record.category.replace('*', '') +
        ' file size cannot exceed ' +
        allowedFileUploadSize +
        ' MB.' +
        newLine;
    }

    if (
      !documentInfo.AllowedTypes.some(x => x.type === fileType) ||
      !documentInfo.AllowedTypes.some(x => x.name === extension)
    ) {
      errorMessage +=
        record.category.replace('*', '') +
        ' must be one of the following file types: ' +
        documentInfo.AllowedTypes.map(x => x.name).join(', ') +
        newLine;
    }

    setFileErrorMessage(errorMessage);
    return !errorMessage;
  };

  const handleFileDownload = async (event, record) => {
    event.preventDefault();

    setFileErrorMessage(null);
    const res = await downloadFile(
      checkPointId,
      record.fileName,
      !(record.id && viewMode === PAGE_VIEW_MODES.EDIT)
    );

    if (res.hasError) {
      setFileErrorMessage('An error occurred while downloading the file.');
      return;
    }
  };

  const handleFileUpload = async (event, file, record) => {
    event.preventDefault();

    setFileErrorMessage(null);
    await executeWithLoadingIndicator(async () => {
      const res = await uploadFile(file, checkPointId, file.name);

      if (res.hasError) {
        setFileErrorMessage('An error occurred while uploading the file.');
        return;
      }

      const updatedDocuments = checkPointCache.checkPointDocuments.concat([
        {
          documentType: record.documentType,
          fileName: file.name
        }
      ]);

      onSetCheckPointDocuments(updatedDocuments);
    });
  };

  const handleFileRemove = (e, record) => {
    e.preventDefault();

    const newDocuments = checkPointCache.checkPointDocuments.filter(
      d => d.documentType !== record.documentType
    );

    onSetCheckPointDocuments(newDocuments);
  };

  const onSetCheckPointDocuments = documents => {
    validateCheckpointDocuments(documents, checkPointCache.dataCollectedDuringAppraisal, true);
    checkPointData({ ...checkPointCache, checkPointDocuments: documents });
  };

  const onSetSelectedDate = date => {
    setValidationErrors(prev => ({ ...prev, hasDateError: false }));
    checkPointData({ ...checkPointCache, dataCollectionDate: date });
  };

  const handleEditPerformanceMetric = tempId => {
    const selectedPerformanceMetric = checkPointCache.performanceMetrics.find(
      x => x.tempId === tempId
    );
    checkPointData({ ...checkPointCache, selectedPerformanceMetric });
  };
  const getValidationErrorMessages = () => {
    const message = [];

    if (validationErrors.hasDateError) {
      message.push(
        checkPointCache.dataCollectionDate
          ? 'Invalid Data Collection Date.'
          : 'Data Collection Date is required.'
      );
    }

    if (validationErrors.hasMetricsError) {
      message.push('Metric errors must be resolved before submission.');
    }

    if (documentValidationErrors.length > 0) {
      message.push(...documentValidationErrors.map(x => x.message));
    }

    return message.join(newLine);
  };

  const handleChangeDataCollectedDuringAppraisal = value => {
    setFileErrorMessage(null);
    checkPointData({ ...checkPointCache, dataCollectedDuringAppraisal: value });
    validateCheckpointDocuments(checkPointCache.checkPointDocuments, value, true);
  };

  if (submitCheckPointError || checkpointAddingDataError) {
    return <ApolloError />;
  }

  const showSaveButton =
    privileges.includes(PrivilegeNames.CheckPoints_SaveDraft) && viewMode === PAGE_VIEW_MODES.ADD;

  return (
    <div>
      <StickyHeader>
        <div className='header-content'>
          <PageInstructions title={title} description={description} />
          <div className='action-group'>
            <div className='action-button-group'>
              <LinkButton
                data-testid='cancel-button'
                className='secondary-button sm'
                to={previousLink}>
                Cancel
              </LinkButton>
              {showSaveButton && (
                <Button
                  disabled={autoSaveInProgress}
                  onClick={handleFormSave}
                  data-testid='save-button'
                  className='primary-button sm'>
                  Save
                </Button>
              )}
              <Button
                disabled={autoSaveInProgress}
                onClick={handleFormSubmit}
                data-testid='submit-button'
                className='primary-button sm'>
                Submit
              </Button>
            </div>
            {autoSaveInProgress && (
              <div className='auto-save-message'>Auto save in progress... </div>
            )}
            {getValidationErrorMessages() && (
              <ErrorMessage
                dataTestId='submit-error-message'
                className='error-message'
                message={getValidationErrorMessages()}
              />
            )}
          </div>
        </div>
      </StickyHeader>
      {location.state?.successMessage && <SuccessMessage message={location.state.successMessage} />}
      <div>
        <p className='bold'>
          Data Collection Date
          <Asterisk />
        </p>
        <p>{datePickerDescription}</p>
        <div className='data-collection-row'>
          <SkeletonWrapper show={addingCheckpointDataLoading}>
            <CustomDatePicker
              id='date-picker'
              minDate={minDate}
              maxDate={maxDate}
              selectedDate={
                checkPointCache.dataCollectionDate &&
                moment(checkPointCache.dataCollectionDate, 'YYYY/MM/DD').toDate()
              }
              setSelectedDate={onSetSelectedDate}
              hasDateError={validationErrors.hasDateError}
            />
          </SkeletonWrapper>

          {canUploadRequiredFiles && (
            <CheckboxField
              title='Collected during Appraisal timeframe.'
              name='dataCollectedDuringAppraisal'
              testId='dataCollectedDuringAppraisal'
              checked={checkPointCache.dataCollectedDuringAppraisal}
              handleChange={(_, value) => handleChangeDataCollectedDuringAppraisal(value)}
              loading={addingCheckpointDataLoading}
            />
          )}
        </div>
      </div>
      <div>
        <CheckpointFilesTable
          viewMode={viewMode}
          handleFileInputClicked={handleFileInputClicked}
          handleFileDownload={handleFileDownload}
          handleFileRemove={handleFileRemove}
          checkPointDocuments={checkPointCache.checkPointDocuments}
          fileErrorMessage={fileErrorMessage}
          documentValidationErrors={documentValidationErrors}
          canUploadRequiredFiles={canUploadRequiredFiles}
          requiredTypes={requiredDocuments}
          loading={loading || addingCheckpointDataLoading}
        />
        <PerformanceMetricsTable
          viewMode={viewMode}
          performanceMetrics={checkPointCache.performanceMetrics}
          invalidMetrics={invalidMetrics}
          orgUnitId={orgUnitId}
          appraisalId={appraisalId}
          loading={loading || addingCheckpointDataLoading}
          handleChange={handleChangeMetricValues}
          handleEditPerformanceMetric={handleEditPerformanceMetric}
          checkPointId={checkPointId}
        />
      </div>
    </div>
  );
};

const getMaxDate = data => {
  return data.MaxDate ? new Date(data.MaxDate) : getToday();
};

const datePickerDescription =
  'Select the date most accurately represents the data collection period. For example, if the ' +
  'data represents the month of January, use January 31.';

export default CheckpointForm;
